/**
 * Global upload store — a module-level pub/sub singleton, because an
 * upload has to outlive the page that started it.
 *
 * Two flows share it, and the design treats them as one interaction
 * (Import/Export Flow V2):
 *
 *   • **create** — Submission Triage: files go up, a NEW submission comes
 *     back. `POST /v2/submissions/from-upload` answers with the
 *     submission id straight away (202) so the panel can title itself
 *     "TRI-89 · New Submission" while extraction is still running; one
 *     job carries every file.
 *   • **attach** — a submission's Files tab: documents are added to a
 *     submission that already exists, one `POST` and one single-file job
 *     per document.
 *
 * Both end up polling the same `GET /v2/submissions/upload/{job_id}`, so
 * downstream everything is a list of file rows moving toward a terminal
 * state. Hence one `batch` phase with a `mode`, rather than two
 * near-identical state machines.
 *
 * Singleton on purpose: the design allows exactly one upload at a time
 * across the whole product, so every entry point can simply ask this
 * store whether it should be disabled.
 */
import {
    attachUnderwritingDoc,
    createSubmissionFromUpload,
    deleteSubmission,
    deleteSubmissionFile,
    importPricingModelWorkbook,
    listSubmissionFiles,
    waitForJob,
    waitForUploadJob,
} from "../api";
import type { ParseTaskStatus } from "../api";

/** One row in the panel.
 *
 *  `id` exists because rows are not stable by position: removing one
 *  re-indexes the array while other files are still in flight, and an
 *  in-flight job that patched by index would land on someone else's row. */
export interface BatchFileState {
    id: string;
    name: string;
    size: number;
    status: "uploading" | "extracting" | "complete" | "failed";
    /** Classification from the worker ("Loss Run", "Acord", …) — drives
     *  which nav sections get flagged after the data is applied. */
    documentType: string | null;
    error: string | null;
}

export type UploadMode = "create" | "attach" | "pricing";

export type UploadStatusState =
    | { phase: "idle" }
    /** An upload in flight or finished but not yet dismissed. On `create`
     *  the submission id is null until the POST answers. */
    | {
          phase: "batch";
          mode: UploadMode;
          submissionId: string | null;
          files: BatchFileState[];
          settled: boolean;
          /** `pricing` only: the workbook is creating a NEW submission
           *  (Triage) rather than overwriting an existing one (Files). The
           *  id arrives when the job completes, unlike `create`, whose POST
           *  returns it up front. */
          pricingCreates?: boolean;
      }
    /** `attach` only: the user pressed Update Submission. The submission
     *  layout consumes this (refetch + toast + nav markers), then
     *  dismisses. `create` needs no equivalent — its CTA just navigates
     *  to the submission the worker already filled in. */
    | { phase: "applied"; submissionId: string; documentTypes: string[] };

let current: UploadStatusState = { phase: "idle" };
const subscribers = new Set<() => void>();

function set(next: UploadStatusState): void {
    current = next;
    subscribers.forEach((cb) => cb());
}

export function getUploadStatusSnapshot(): UploadStatusState {
    return current;
}

export function subscribeUploadStatus(cb: () => void): () => void {
    subscribers.add(cb);
    return () => {
        subscribers.delete(cb);
    };
}

/** True while an upload owns the product's single upload slot — every
 *  Upload / Auto-fill entry point disables itself on this. */
export function isUploadBusy(state: UploadStatusState = current): boolean {
    return state.phase === "batch" && !state.settled;
}

/** Bumped whenever this store changes a submission's stored FILES: a
 *  batch settling (the worker saved the documents) or a row being
 *  removed (we deleted one). The Files table watches it, since removing
 *  one row of several leaves the phase itself untouched. */
let filesRevision = 0;

export function getSubmissionFilesRevision(): number {
    return filesRevision;
}

function bumpFilesRevision(): void {
    filesRevision++;
    subscribers.forEach((cb) => cb());
}

/** Clear a settled panel. No-op while files are still moving — that path
 *  goes through `cancelBatch`, behind its own confirmation.
 *
 *  Also drops the batch's File handles: a create batch ends here (via
 *  View Submission) rather than through `applyBatch`, so without this the
 *  singleton would hold every uploaded blob in memory until the next
 *  batch or a reload. */
export function dismissUploadStatus(): void {
    if (isUploadBusy()) return;
    batchFiles.clear();
    set({ phase: "idle" });
}

// ─────────────────────────── batch plumbing ─────────────────────────────

/** The batch's File handles, kept out of render state so a failed row can
 *  be retried without re-picking. `token` invalidates in-flight polls
 *  after a cancel: the queued jobs themselves cannot be aborted, but the
 *  UI stops tracking them. */
let batchFiles = new Map<string, File>();
let batchToken = 0;

/** Throw away a submission a cancelled create batch left behind.
 *  Fire-and-forget: the panel is already gone, and the queued extraction
 *  job cannot be called back (it simply finds nothing to write to). */
function discardSubmission(submissionId: string): void {
    void deleteSubmission(submissionId).catch((error: unknown) => {
        console.warn("Couldn't delete the submission left by a cancelled upload", error);
    });
}

/** Per-file parse states → row states. `stored` is a SUCCESS: the
 *  document was recognised and kept, it is simply a type we deliberately
 *  do not extract (a licence, say). Treating it as a failure would tell
 *  the user their upload broke when it did exactly what it should. */
function rowStatusFor(task: ParseTaskStatus): BatchFileState["status"] {
    if (task === "parsed" || task === "stored") return "complete";
    if (task === "failed") return "failed";
    return "extracting";
}

function patchRows(update: (files: BatchFileState[]) => BatchFileState[]): void {
    if (current.phase !== "batch") return;
    const files = update(current.files);
    const settled = files.length > 0 && files.every((f) => f.status === "complete" || f.status === "failed");
    const wasSettled = current.settled;
    set({ ...current, files, settled });
    if (settled && !wasSettled) bumpFilesRevision();
}

function patchRow(id: string, patch: Partial<BatchFileState>): void {
    patchRows((files) => files.map((f) => (f.id === id ? { ...f, ...patch } : f)));
}

let nextRowId = 0;

function rowsFor(files: File[]): BatchFileState[] {
    return files.map((f) => ({
        id: `row-${nextRowId++}`,
        name: f.name,
        size: f.size,
        status: "uploading" as const,
        documentType: null,
        error: null,
    }));
}

// ───────────────────────────── create flow ──────────────────────────────

/**
 * Submission Triage: create a submission from the picked files.
 *
 * One POST for the whole batch; the response carries the new submission
 * id immediately, so the panel can name itself before extraction ends.
 * A single job then reports every file, matched back to its row by name.
 */
export function startCreateBatch(files: File[]): boolean {
    if (isUploadBusy() || files.length === 0) return false;
    const rows = rowsFor(files);
    batchFiles = new Map(rows.map((r, i) => [r.id, files[i]]));
    const token = ++batchToken;
    set({ phase: "batch", mode: "create", submissionId: null, files: rows, settled: false });

    void (async () => {
        try {
            const created = await createSubmissionFromUpload(files);
            if (token !== batchToken) {
                // Cancelled while this POST was still open, so `cancelBatch`
                // had no id to clean up: it only ever sees what reached the
                // store. The submission exists all the same, and this is the
                // only place that knows about it.
                discardSubmission(created.submission_id);
                return;
            }
            if (current.phase === "batch") set({ ...current, submissionId: created.submission_id });

            const result = await waitForUploadJob(created.job_id, {
                onPoll: (job) => {
                    if (token !== batchToken || current.phase !== "batch") return;
                    patchRows((rows) =>
                        rows.map((row) => {
                            const task = job.files.find((t) => t.file_name === row.name);
                            if (!task) return row;
                            return {
                                ...row,
                                status: rowStatusFor(task.status),
                                documentType: task.document_type ?? row.documentType,
                                error: task.error ?? row.error,
                            };
                        }),
                    );
                },
            });
            if (token !== batchToken) return;
            if (result === "timeout") {
                patchRows((rows) =>
                    rows.map((r) => (r.status === "complete" ? r : { ...r, status: "failed", error: "Extraction timed out." })),
                );
            }
        } catch (error) {
            if (token !== batchToken) return;
            console.error("Create-from-upload failed", error);
            patchRows((rows) => rows.map((r) => ({ ...r, status: "failed", error: r.error })));
        }
    })();
    return true;
}

// ───────────────────────────── attach flow ──────────────────────────────

/** Upload one file of an attach batch and follow its own single-file job. */
async function driveAttachFile(submissionId: string, rowId: string, token: number): Promise<void> {
    const file = batchFiles.get(rowId);
    if (!file) return;
    try {
        const job = await attachUnderwritingDoc(submissionId, file);
        if (token !== batchToken) return;
        patchRow(rowId, { status: "extracting" });
        const result = await waitForUploadJob(job.job_id, { intervalMs: 5000 });
        if (token !== batchToken) return;
        if (result === "timeout") {
            patchRow(rowId, { status: "failed", error: "Extraction timed out. Try again." });
            return;
        }
        const task = result.files[0];
        patchRow(rowId, {
            status: task ? rowStatusFor(task.status) : "failed",
            documentType: task?.document_type ?? null,
            error: task?.error ?? null,
        });
    } catch (error) {
        if (token !== batchToken) return;
        console.error("Attach upload failed", error);
        patchRow(rowId, { status: "failed", error: error instanceof Error ? error.message : null });
    }
}

/** A submission's Files tab: add documents to a submission that exists. */
export function startAttachBatch(submissionId: string, files: File[]): boolean {
    if (isUploadBusy() || files.length === 0) return false;
    const rows = rowsFor(files);
    batchFiles = new Map(rows.map((r, i) => [r.id, files[i]]));
    const token = ++batchToken;
    set({ phase: "batch", mode: "attach", submissionId, files: rows, settled: false });
    rows.forEach((r) => void driveAttachFile(submissionId, r.id, token));
    return true;
}

// ───────────────────────────── pricing flow ─────────────────────────────

/** Drive one workbook through the import job (both pricing variants). */
async function drivePricingFile(rowId: string, token: number, submissionId: string | null): Promise<void> {
    const file = batchFiles.get(rowId);
    if (!file) return;
    try {
        const { job_id } = await importPricingModelWorkbook(file, submissionId ?? undefined);
        if (token !== batchToken) return;
        patchRow(rowId, { status: "extracting" });
        const job = await waitForJob(job_id);
        if (token !== batchToken) return;
        if (job === "timeout") {
            patchRow(rowId, { status: "failed", error: "Import timed out. Try again." });
            return;
        }
        if (job.status === "failed") {
            // Cell-level problems ride the job's error string.
            patchRow(rowId, { status: "failed", error: job.error ?? "The workbook could not be imported." });
            return;
        }
        // Triage variant: the new submission's id only exists now.
        const created = job.result?.submission_id;
        if (current.phase === "batch" && current.mode === "pricing" && !current.submissionId && typeof created === "string") {
            set({ ...current, submissionId: created });
        }
        patchRow(rowId, { status: "complete", documentType: "Pricing Model", error: null });
    } catch (error) {
        if (token !== batchToken) return;
        // A malformed/oversized upload is rejected before any job exists —
        // the 422's formatted message lands here.
        console.error("Pricing model import failed", error);
        patchRow(rowId, { status: "failed", error: error instanceof Error ? error.message : null });
    }
}

/**
 * Import a pricing-model workbook through the floating panel.
 *
 * With a `submissionId` (Files) the import overwrites that submission's
 * imported sections and the panel's CTA is Update Submission; without one
 * (Triage) the job creates a new submission and the CTA navigates to it.
 * One file per batch — the backend takes a single workbook per request.
 */
export function startPricingImport(file: File, submissionId?: string): boolean {
    if (isUploadBusy()) return false;
    const rows = rowsFor([file]);
    batchFiles = new Map([[rows[0].id, file]]);
    const token = ++batchToken;
    set({
        phase: "batch",
        mode: "pricing",
        submissionId: submissionId ?? null,
        files: rows,
        settled: false,
        pricingCreates: !submissionId,
    });
    void drivePricingFile(rows[0].id, token, submissionId ?? null);
    return true;
}

// ──────────────────────────── row actions ───────────────────────────────

/** Re-run one failed row (the panel's "Try again"). Attach and pricing
 *  can retry a single file; create sends the whole batch in one POST, so
 *  a failed row there is dropped instead. */
export function retryBatchFile(id: string): void {
    if (current.phase !== "batch" || current.mode === "create") return;
    if (current.files.find((f) => f.id === id)?.status !== "failed") return;
    if (current.mode === "pricing") {
        const submissionId = current.pricingCreates ? null : current.submissionId;
        patchRow(id, { status: "uploading", error: null });
        void drivePricingFile(id, batchToken, submissionId);
        return;
    }
    if (!current.submissionId) return;
    patchRow(id, { status: "uploading", error: null });
    void driveAttachFile(current.submissionId, id, batchToken);
}

/**
 * Drop one row (the panel's trash).
 *
 * Both flows store the document before extraction runs, so a row that
 * failed to EXTRACT still left a file behind; removing the row deletes
 * that record too. Best-effort: the row goes either way, since the panel
 * is the user's only handle on it. Resolved by name, newest match first,
 * so a Keep-both duplicate does not take its older twin down with it.
 */
export function removeBatchFile(id: string): void {
    if (current.phase !== "batch") return;
    const target = current.files.find((f) => f.id === id);
    const submissionId = current.submissionId;
    if (!target) return;

    batchFiles.delete(id);
    const files = current.files.filter((f) => f.id !== id);
    if (files.length === 0) {
        batchToken++;
        set({ phase: "idle" });
    } else {
        patchRows(() => files);
    }

    if (!submissionId) return;
    void (async () => {
        try {
            const stored = await listSubmissionFiles(submissionId);
            const match = stored.filter((f) => f.name === target.name).sort((a, b) => (a.uploadedAt < b.uploadedAt ? 1 : -1))[0];
            if (match) {
                await deleteSubmissionFile(submissionId, match.id);
                bumpFilesRevision();
            }
        } catch (error) {
            console.warn("Couldn't delete the stored file for a removed upload row", error);
        }
    })();
}

/**
 * Abandon an in-flight batch (Cancel Upload).
 *
 * On `create` this has to reach the server: `from-upload` answers with a
 * real submission before extraction starts, so walking away client-side
 * would strand a half-filled submission in Triage that the user believes
 * they cancelled. The dialog promises "You'll lose your progress", so
 * the submission goes with it. Best-effort and fire-and-forget: the
 * panel closes either way, and the queued extraction job cannot be
 * called back (it simply finds nothing to write to).
 *
 * On `attach` there is nothing to undo here — the submission predates
 * the batch, and documents already stored are removed per row, through
 * the trash.
 */
export function cancelBatch(): void {
    if (current.phase !== "batch") return;
    const orphaned = current.mode === "create" ? current.submissionId : null;
    batchToken++;
    batchFiles.clear();
    set({ phase: "idle" });
    if (orphaned) discardSubmission(orphaned);
}

// ─────────────────────────── applying the data ──────────────────────────

/**
 * Which nav sections a document type feeds — the "highlighted sections"
 * the toast points at. Keys match the backend's `DocumentType`.
 *
 * Only the three types the backend actually EXTRACTS belong here, the
 * ones with a handler in `submission_upload/handlers`. License, Survey,
 * Resume and Schedule of Insureds are `STORE_ONLY_TYPES`: the worker
 * recognises and keeps them, deliberately without reading them (surveys,
 * for instance, are ingested from CMS instead). They finish successfully,
 * so marking a section for them would promise data that was never
 * written — do not add them back.
 *
 * One known gap: a loss run can also fill a missing broker name, which
 * lives in UW Information. It is a fallback the ACORD normally wins, so
 * flagging UW Information on every loss run would cry wolf far more
 * often than it would help. Getting this exactly right needs the backend
 * to report which sections it wrote, rather than the FE inferring it
 * from the document type.
 */
const DOC_TYPE_SECTIONS: Record<string, readonly string[]> = {
    "Loss Run": ["loss-runs"],
    Acord: ["uw-information", "schedule"],
    "Supplemental Application": ["uw-information"],
    // The workbook import overwrites locations, loss runs, the quote and
    // the core fields in one transaction (see documents.ts).
    "Pricing Model": ["uw-information", "schedule", "loss-runs", "premium-selection"],
};

export function sectionsForDocumentTypes(documentTypes: readonly string[]): Set<string> {
    const sections = new Set<string>();
    for (const type of documentTypes) for (const s of DOC_TYPE_SECTIONS[type] ?? []) sections.add(s);
    return sections;
}

/** Update Submission pressed (attach only): hand the outcome to the
 *  submission layout, which refetches and shows the toast + markers. */
export function applyBatch(): void {
    if (current.phase !== "batch" || !current.settled || !current.submissionId) return;
    const documentTypes = current.files.filter((f) => f.status === "complete").map((f) => f.documentType ?? "Unknown");
    batchFiles.clear();
    set({ phase: "applied", submissionId: current.submissionId, documentTypes });
}
